import { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Satellite, KeyRound, Save, RefreshCw, Check, AlertTriangle, Lock, Loader2, X, PlugZap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Settings → Integrations → Samsara. Mirrors the 8x8/Zoho credentials
 * write-only pattern exactly (encrypted at rest, password step-up to edit,
 * never sent back to the browser in plaintext) — powers the Transport map's
 * live tracking overlay + the geofence auto-complete checker.
 */
export const SamsaraSettings = () => {
  const [statuses, setStatuses] = useState({});
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [showStepUp, setShowStepUp] = useState(false);
  const [stepUpPassword, setStepUpPassword] = useState('');
  const [stepUpLoading, setStepUpLoading] = useState(false);
  const [stepUpToken, setStepUpToken] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/admin/integrations/samsara`);
      setStatuses(res.data.statuses || {});
      setMeta({ updated_at: res.data.updated_at, updated_by_email: res.data.updated_by_email });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load Samsara status'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchStatus(); }, []);

  const isSet = !!statuses.SAMSARA_API_KEY?.is_set;

  const openStepUp = () => setShowStepUp(true);
  const submitStepUp = async () => {
    if (!stepUpPassword.trim()) return toast.error('Password is required');
    setStepUpLoading(true);
    try {
      const res = await api.post(`${API}/admin/integrations/samsara/verify-step-up`, { password: stepUpPassword, totp_code: '' });
      setStepUpToken(res.data.step_up_token);
      setShowStepUp(false);
      setEditing(true);
      setEditValue('');
      toast.success('Verified — edit mode unlocked for 5 minutes');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verification failed — password incorrect'); }
    finally { setStepUpLoading(false); setStepUpPassword(''); }
  };
  const cancelEdit = () => { setEditing(false); setEditValue(''); setStepUpToken(null); };
  const onSave = async () => {
    if (!stepUpToken) { toast.error('Re-verify to save'); return openStepUp(); }
    if (!editValue.trim()) return toast.error('Enter the API key to save');
    setSaving(true);
    try {
      await api.put(`${API}/admin/integrations/samsara`, { step_up_token: stepUpToken, credentials: { SAMSARA_API_KEY: editValue } });
      toast.success('Samsara API key saved');
      setStepUpToken(null); setEditing(false); setEditValue('');
      await fetchStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); setStepUpToken(null); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await api.get(`${API}/admin/integrations/samsara/test`);
      toast.success(`Connected — ${res.data.vehicle_count} vehicle(s) visible to this key`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Connection test failed'); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-5" data-testid="samsara-settings">
      <div className="rounded-xl border-2 border-sky-200 bg-sky-50 p-4 flex items-start gap-3">
        <Satellite className="w-6 h-6 text-sky-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-bold text-sky-900 mb-0.5">Samsara Live Tracking — {isSet ? 'Connected' : 'Awaiting API key'}</div>
          <div className="text-sky-800">
            Paste your Samsara API token below to power live vehicle positions on today's Transport map, completed
            routes for past days, and automatic drop-completion when a driver arrives and leaves a stop. Map each
            vehicle to its Samsara ID in Settings → Vehicles.
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Samsara API Key</h3>
            {meta.updated_at && (
              <p className="text-xs text-gray-500 mt-0.5">
                Last updated {new Date(meta.updated_at).toLocaleString('en-GB')}{meta.updated_by_email ? ` by ${meta.updated_by_email}` : ''}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {isSet && !editing && (
              <Button variant="outline" size="sm" onClick={testConnection} disabled={testing} data-testid="samsara-test-btn">
                {testing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlugZap className="w-3.5 h-3.5 mr-1" />} Test Connection
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading} data-testid="samsara-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {!editing && (
              <Button size="sm" onClick={openStepUp} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="samsara-edit-btn">
                <KeyRound className="w-3.5 h-3.5 mr-1" /> {isSet ? 'Edit Key' : 'Add Key'}
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" size="sm" onClick={cancelEdit}><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
                <Button size="sm" onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="samsara-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
                </Button>
              </>
            )}
          </div>
        </div>

        {!editing ? (
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm font-semibold text-gray-800">API Key</span>
            {isSet
              ? <span className="flex items-center gap-1 text-emerald-600 text-xs font-semibold"><Check className="w-3.5 h-3.5" /> Set</span>
              : <span className="flex items-center gap-1 text-red-500 text-xs font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Not set</span>}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
              <Lock className="inline w-3 h-3 mr-1" /> Hidden as you type. Leave blank and cancel to keep the existing value.
            </p>
            <Label className="text-xs font-semibold text-gray-700">
              API Key {isSet ? <span className="text-emerald-600 font-normal">(currently set)</span> : <span className="text-red-500 font-normal">(not set)</span>}
            </Label>
            <Input
              type="password" autoComplete="new-password"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder={isSet ? 'Leave blank to keep existing value' : 'Paste Samsara API token'}
              className="font-mono text-sm"
              data-testid="samsara-edit-api-key"
            />
          </div>
        )}
      </div>

      <Dialog open={showStepUp} onOpenChange={(o) => !o && setShowStepUp(false)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-blue-600" /> Security step-up required</DialogTitle>
            <DialogDescription>Re-enter your login password to unlock Samsara credential editing for 5 minutes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Password</Label>
              <Input type="password" autoFocus value={stepUpPassword} onChange={(e) => setStepUpPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitStepUp()} data-testid="samsara-stepup-password" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowStepUp(false)}>Cancel</Button>
            <Button onClick={submitStepUp} disabled={stepUpLoading || !stepUpPassword.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="samsara-stepup-submit">
              {stepUpLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verifying…</> : 'Verify'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SamsaraSettings;
